import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { ArtModal } from '../components/ArtModal';
import { PixelDragon64 } from '../components/PixelDragon64';
import { theme } from '../theme';
import { assetUrl } from '../utils/assetUrl';

const Wrapper = styled.section`
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  gap: ${theme.space[6]}px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const IntroCard = styled.article`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md}px;
  padding: ${theme.space[5]}px;
`;

const Heading = styled.h1`
  margin: 0 0 ${theme.space[3]}px;
  font-family: ${theme.fonts.display};
  font-size: ${theme.fontSizes.hero};
  line-height: 1.05;
`;

const Intro = styled.p`
  margin: 0 0 ${theme.space[3]}px;
  color: ${theme.colors.muted};
  line-height: 1.65;
`;

const ButtonRow = styled.div`
  margin-top: ${theme.space[4]}px;
`;

const PortfolioLink = styled(Link)`
  display: inline-block;
  text-decoration: none;
  border: 1px solid ${theme.colors.accent};
  color: ${theme.colors.accent};
  padding: 8px 14px;
  border-radius: ${theme.radii.pill}px;
  font-size: ${theme.fontSizes.sm};
  letter-spacing: 0.04em;
  text-transform: uppercase;

  &:hover {
    background: ${theme.colors.accentSoft};
    text-decoration: none;
  }
`;

const FeatureCard = styled.button`
  appearance: none;
  border: 1px solid ${theme.colors.border};
  background: ${theme.colors.surface};
  border-radius: ${theme.radii.md}px;
  padding: 0;
  cursor: pointer;
  text-align: left;
  overflow: hidden;

  &:hover {
    border-color: ${theme.colors.accent};
  }
`;

const FeatureLabel = styled.div`
  font-size: ${theme.fontSizes.xs};
  color: ${theme.colors.muted};
  padding: ${theme.space[3]}px ${theme.space[4]}px 0;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const FeatureImageWrap = styled.div`
  background: ${theme.colors.gallery};
  margin-top: ${theme.space[2]}px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FeatureImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
  image-rendering: pixelated;
  image-rendering: crisp-edges;
`;

const FeatureInfo = styled.div`
  padding: ${theme.space[4]}px;
`;

const FeatureTitle = styled.h2`
  margin: 0 0 ${theme.space[1]}px;
  font-family: ${theme.fonts.display};
  font-size: ${theme.fontSizes.xl};
`;

const FeatureDate = styled.time`
  display: block;
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.muted};
`;

const Loading = styled.p`
  color: ${theme.colors.muted};
`;

export function Home() {
  const [latest, setLatest] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL}/art/art.json`)
      .then((res) => {
        if (!res.ok) throw new Error('Could not load art');
        return res.json();
      })
      .then((items) => {
        const sorted = [...items].sort((a, b) => Number(b.id) - Number(a.id));
        setLatest(sorted[0] || null);
      })
      .catch(() => setLatest(null));
  }, []);

  return (
    <>
      <Wrapper>
        <IntroCard>
          <Heading>
            Hey, I am <PixelDragon64 />.
          </Heading>
          <Intro>
            I have been doing pixel art on and off for over 10 years, creating sprites, characters, and worlds  for various projects.
          </Intro>
          <Intro>
            I have also worked at game studios both small and large on game development
            and design. Nowadays I am independent and have a mysterious source of income.
          </Intro>
          <Intro>
          I'm currently working on a 2 year ongoing D&D campaign, as well as a lot of smaller game projects.
          </Intro>
          <ButtonRow>
            <PortfolioLink to="/portfolio">View Portfolio</PortfolioLink>
          </ButtonRow>
        </IntroCard>

        {latest ? (
          <FeatureCard type="button" onClick={() => setSelected(latest)} aria-label={`Open ${latest.title}`}>
            <FeatureLabel>Featured Latest Piece</FeatureLabel>
            <FeatureImageWrap>
              <FeatureImage src={assetUrl(latest.imageUrl)} alt={latest.title} />
            </FeatureImageWrap>
            <FeatureInfo>
              <FeatureTitle>{latest.title}</FeatureTitle>
              <FeatureDate dateTime={latest.date}>
                {new Date(latest.date).toLocaleDateString('en-US', {
                  month: 'long',
                  year: 'numeric',
                })}
              </FeatureDate>
            </FeatureInfo>
          </FeatureCard>
        ) : (
          <Loading>Loading latest piece…</Loading>
        )}
      </Wrapper>

      {selected && <ArtModal piece={selected} onClose={() => setSelected(null)} />}
    </>
  );
}

